import { App } from '../../../../App';
import {
  Button,
  Divider,
  InformationIcon,
  Spacer,
  Text,
} from 'internalLibrary';
import { FC, useMemo } from 'react';
import { formatCurrencyOrMock } from '../../../../formatters/formatCurrencyOrMock';
import { formatNumberToLocaleOrMock } from '../../../../formatters/formatNumberToLocaleOrMock';
import { Tooltip } from 'react-tooltip';
import { WarningRed2Icon } from '../../../../assets';
import BusinessInfo from '../../../../components/BusinessInfo/BusinessInfo';
import classNames from 'classnames';
import DesktopProductProperty from '../DesktopProductProperty/DesktopProductProperty';
import styles from './Sidebar.module.scss';
import useAmountConstraints from '../LoanCalculatorSettings/useAmountConstraints';
import useLoanApplicationProcess from '../LoanApplicationProcessProvider/useLoanApplicationProcess';
import useLoanOffer from './useLoanOffer';

interface SidebarProps {
  onModifyAmountClick: () => void;
}

const Sidebar: FC<SidebarProps> = (props) => {
  const { onModifyAmountClick } = props;
  const t = App.translateContent('leftPanel');
  const tCommon = App.translateContent('common');
  const tContact = App.translateContent('contact');
  const { status: statusAmountConstraints } = useAmountConstraints();
  const { loanOffer, status } = useLoanOffer();
  const { loanAmount, progress } = useLoanApplicationProcess();

  const hideEditButton = progress > 0.33;
  const loading =
    !loanOffer || status === 'loading' || statusAmountConstraints === 'loading';

  const loadingClassNames = useMemo(
    () =>
      classNames(
        styles['value-loading'],
        status === 'loading' ? undefined : styles['value-loading--done'],
      ),
    [loading],
  );

  const daysToPaymentText = useMemo(() => {
    return (
      <span>
        {t.daysToPayment}:{' '}
        <span
          data-test="sidebar-days-to-repayment"
          className={classNames(
            styles['value-loading'],
            loading ? undefined : styles['value-loading--done'],
          )}
        >
          {loanOffer?.term || '##'}
        </span>
      </span>
    );
  }, [loading, loanOffer?.term, t.daysToPayment]);

  return (
    <>
      <div className={styles.summary}>
        <div className={styles.title} data-test="xpath-Title">
          {t.loanSummary}
        </div>
        <Spacer marginTop={'4px'}>
          <Divider />
        </Spacer>
        <DesktopProductProperty
          main
          loading={loading}
          value={formatNumberToLocaleOrMock(loanAmount, '## ###', loading)}
          valueSuffix={tCommon.currencySymbol}
          dataTestAmount="sidebar-loan-amount"
          label={
            <div className={styles['main-value-label']}>
              <span>{t.creditAmount}</span>
              <InformationIcon
                className={styles['info-icon']}
                id={'creditTooltip'}
              />
            </div>
          }
        />
        <DesktopProductProperty
          main
          loading={loading}
          value={
            <div className={styles['main-value-value']}>
              {loading ? `##-##-##` : loanOffer?.agreementEndDate}
            </div>
          }
          label={
            <div className={styles['main-value-label']}>
              <span>{daysToPaymentText}</span>
              <InformationIcon
                className={styles['info-icon']}
                id={'daysToPaymentTooltip'}
              />
            </div>
          }
        />
        {hideEditButton ? (
          <Spacer marginVertical="10px">
            <Divider />
          </Spacer>
        ) : (
          <Button
            small
            outlined
            onClick={onModifyAmountClick}
            dataTest="css-Edit-Button"
          >
            {t.change}
          </Button>
        )}
        <div className={styles.notice}>
          <Text
            dangerouslySetInnerHTML={{ __html: t.representativeExample }}
            kind="p"
            className={styles.representative}
          />
          <WarningRed2Icon className={styles['warning-icon']} />
          <h4>{t.highCostCredit}</h4>
          <Text
            kind="p"
            dangerouslySetInnerHTML={{
              __html: t.highCostCreditDescription,
            }}
          />
        </div>

        <DesktopProductProperty
          loading={loading}
          dataTestAmount="sidebar-interest-amount"
          value={formatNumberToLocaleOrMock(
            loanOffer?.firstPaymentInterest,
            '###,#',
            loading,
          )}
          valueSuffix={tCommon.currencySymbol}
          label={t.interest}
        />
        <DesktopProductProperty
          loading={loading}
          dataTestAmount="sidebar-setup-fee-amount"
          value={formatNumberToLocaleOrMock(
            loanOffer?.additionalAmountAdministrationFee,
            '###',
            loading,
          )}
          valueSuffix={tCommon.currencySymbol}
          label={t.setupFee}
        />
        <DesktopProductProperty
          loading={loading}
          dataTestAmount="sidebar-effective-interest-rate-amount"
          value={formatNumberToLocaleOrMock(
            loanOffer?.annualPercentageRate,
            '##,#',
            loading,
          )}
          valueSuffix={tCommon.percentSymbol}
          label={t.effectiveInterestRate}
        />
        <DesktopProductProperty
          loading={loading}
          dataTestAmount="sidebar-to-pay-amount"
          value={formatNumberToLocaleOrMock(
            loanOffer?.totalRepayableAmount,
            '## ###,#',
            loading,
          )}
          valueSuffix={tCommon.currencySymbol}
          label={t.toPay}
        />
        <DesktopProductProperty
          loading={loading}
          dataTestAmount="sidebar-least-amount-to-pay-amount"
          value={formatNumberToLocaleOrMock(
            loanOffer?.mtpAmount,
            '# ###,##',
            loading,
          )}
          valueSuffix={tCommon.currencySymbol}
          label={t.leastAmountToPay}
        />

        {!hideEditButton && (
          <>
            <Divider />
            <div className={styles.detail}>
              <div>{t.amortization}</div>
              <div
                className={loadingClassNames}
                data-test="sidebar-amortization-amount"
              >
                {formatCurrencyOrMock(
                  loanOffer?.mtpLoanAmortizationAmount,
                  '###,##',
                  loading,
                )}
              </div>
            </div>
            <div className={styles.detail}>
              <div>{t.interest}</div>
              <div
                className={loadingClassNames}
                data-test="sidebar-interest-detail-amount"
              >
                {formatCurrencyOrMock(
                  loanOffer?.mtpInterestAmount,
                  '###,##',
                  loading,
                )}
              </div>
            </div>
            <div className={styles.detail}>
              <div>{t.accountingFeeForPartialPayment}</div>
              <div className={loadingClassNames}>
                {formatCurrencyOrMock(
                  loanOffer?.mtpAdministrationFee,
                  '#',
                  loading,
                )}
              </div>
            </div>
            <div className={styles.detail}>
              <div>{t.invoiceFee}</div>
              <div className={loadingClassNames}>
                {formatCurrencyOrMock(loanOffer?.mtpInvoicingFee, '#', loading)}
              </div>
            </div>
            <div className={styles.detail}>
              <div>{t.setupFee}</div>
              <div
                className={loadingClassNames}
                data-test="sidebar-administration-fee-detail-amount"
              >
                {formatCurrencyOrMock(
                  loanOffer?.mainAmountAdministrationFee,
                  '###',
                  loading,
                )}
              </div>
            </div>
          </>
        )}

        <Divider />
        <div className={styles['credit-control']}>
          <img src="/images/credit-safe-logo.png" alt={t.creditSafeLogoAlt} />
        </div>

        <div className={styles.footer}>
          <BusinessInfo
            className={styles['mobile-footer']}
            businessHours={tContact.businessHours}
            contactMail={tContact.email}
            contactPhone={tContact.phone}
          />
        </div>
      </div>
      <Tooltip
        anchorId="creditTooltip"
        html={t.creditTooltip}
        variant="light"
        place="right"
        className={styles.tooltip}
      />
      <Tooltip
        anchorId="daysToPaymentTooltip"
        html={t.daysToPaymentTooltip}
        variant="light"
        place="right"
        className={styles.tooltip}
      />
    </>
  );
};

export default Sidebar;
